import {
  AlertTriangle, Check, Copy, ExternalLink, Eye, EyeOff, Image as ImageIcon,
  Gauge, PenLine, Plus, RotateCcw, Search, Send, Trash2, X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchText, get, post } from '../api';
import { useAsk } from '../components/Ask';
import { Empty, ErrorBox, Loading, Modal, Spinner } from '../components/base';
import { formatDate, formatDateTime, formatDuration, formatNumber, formatTimestamp } from '../formats';

/**
 * Content Studio: the review screen.
 *
 * Everything here is one product's, resolved server-side from the id in the
 * URL. The reader sees only what they may act on: a draft still with snip.work
 * shows a client login its state and no buttons, rather than hiding it and
 * leaving them wondering where it went.
 */

type Decision = 'approve' | 'reject' | 'changes';

interface Topic {
  id: string; slug: string; title: string; angle: string | null;
  hub: string | null; channel: string; rationale: string | null;
  sources: string[] | null; status: string; created_at: string;
  updated_at?: string;
  /**
   * Claimed by a run that never finished. Computed by the API from how long it
   * has been 'drafting', not stored - a run in progress and a run that died
   * look identical in the row itself.
   */
  stalled?: boolean;
  /** Research-time scores (1-5, null = unscored) and the date the piece stops
   *  being useful (null = evergreen). The runner will not claim a topic past
   *  its valid_until, so an expired card is the reviewer's to archive or
   *  re-date. */
  relevance?: number | null; urgency?: number | null; evidence?: number | null;
  valid_until?: string | null;
  /** Set while this topic's published article is being refreshed in place.
   *  The reason is research's, for the person deciding whether it is due. */
  refresh_requested_at?: string | null; refresh_reason?: string | null;
}

interface DraftLang {
  lang: string; title: string; description: string | null; tldr: string[] | null;
  word_count: number | null; read_minutes: number | null;
  preview_url: string | null; published_url: string | null;
  /** Which renderings exist. A button is only offered for one that resolves. */
  has_page: number; has_email: number;
}

type PreviewVariant = 'page' | 'email';

interface Source { n?: number; publisher?: string; title?: string; url?: string }

interface Draft {
  id: string; topic_id: string; revision: number; status: string;
  stage: string | null; verify_passed: number; channel: string;
  verify_report: Record<string, unknown> | null;
  sources: Source[] | null; error: string | null; title: string; created_at: string;
  /** Last change, so a revised draft does not read as the original. */
  updated_at?: string;
  langs: DraftLang[]; can_decide: boolean;
}

interface AudienceCount { lang: string; n: number }

interface Run {
  id: string; kind: string; status: string; ref: string | null;
  detail: Record<string, unknown> | null; run_url: string | null;
  started_at: string; finished_at: string | null;
}

interface Queue {
  stages: string[]; languages: string[]; subscribers: AudienceCount[];
  topics: Topic[]; drafts: Draft[]; runs: Run[];
  /**
   * Which pipeline buttons to draw. Two booleans rather than the configuration
   * behind them: a client login must never learn a repository name, so the API
   * answers "can this be started" and keeps "by dispatching draft.yml on
   * <repo>" to itself. Optional because an older API will not send it.
   */
  can_start?: { research: boolean; draft: boolean };
}

const STAGE_LABEL: Record<string, string> = {
  internal: 'With snip.work',
  client: 'With you',
};

const NEUTRAL_CHIP = 'bg-paper-200 text-ink-600 dark:bg-ink-700 dark:text-paper-300';
const STALLED_CHIP = 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-300';

/**
 * Every state a topic can be in, named the way a reader would say it.
 *
 * All six are shown. The Topics tab used to render only 'proposed' and
 * 'approved', so a topic disappeared the moment a run claimed it - on
 * 2026-08-12 two did, and counting what was left gave four where there had
 * been six. Nothing had been lost; a third of the queue was simply not drawn.
 * A screen that shows only what you can act on cannot answer "where did that
 * go", and that is a question people ask of their own data.
 */
const TOPIC_STATE: Record<string, { label: string; chip: string }> = {
  proposed: { label: 'Waiting for you', chip: NEUTRAL_CHIP },
  approved: {
    label: 'Approved, waiting to be drafted',
    chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
  },
  drafting: { label: 'Being drafted now', chip: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300' },
  done: { label: 'Published', chip: NEUTRAL_CHIP },
  rejected: { label: 'You said no', chip: NEUTRAL_CHIP },
  archived: { label: 'Archived', chip: NEUTRAL_CHIP },
};

/**
 * "Something is happening to this right now."
 *
 * A topic used to leave the screen when a run claimed it. It stays now - but
 * staying still is its own kind of wrong: between pressing "Draft this now"
 * and the runner claiming the topic there are ten to twenty seconds in which
 * the card looks exactly as it did before the click, and the honest reading of
 * that is "nothing happened".
 *
 * role="status" so the change is announced rather than only drawn, and the
 * ping layer is dropped under prefers-reduced-motion - a pulsing dot is decor
 * for most people and a problem for some, and the label carries the meaning
 * either way.
 */
function Working({ label }: { label: string }) {
  return (
    <span
      role="status"
      className="badge inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75 motion-reduce:hidden" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
      </span>
      {label}
    </span>
  );
}

/**
 * How often the queue re-reads itself while work is in flight, and for how long.
 *
 * Bounded on both axes because D1 meters rows read and the queue is four
 * queries - see docs/CAPACITY.md, where rows written per day is the binding
 * limit but reads are not free either. Ten seconds is well inside the two to
 * four minutes a draft run takes, and twenty minutes is long enough to cover a
 * slow one while stopping a tab left open overnight from polling until morning.
 *
 * It also stops entirely when nothing is running, which is the usual state.
 */
const POLL_MS = 10_000;
const POLL_CEILING_MS = 20 * 60_000;

/**
 * The judgeable part of a source URL. A reviewer deciding "is this topic
 * researched from the right places" reads domains, not full URLs - the full
 * URL stays in the link and its title.
 */
function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** The order the tab groups them in: what needs you, then what is in flight. */
const TOPIC_GROUPS: { key: string; heading: string; statuses: string[] }[] = [
  { key: 'now', heading: 'Waiting for you', statuses: ['proposed'] },
  { key: 'flight', heading: 'In the pipeline', statuses: ['drafting', 'approved'] },
  { key: 'closed', heading: 'Finished and declined', statuses: ['done', 'rejected', 'archived'] },
];

// -- the preview --------------------------------------------------------------

/**
 * The rendered draft, in a sandboxed iframe.
 *
 * The `sandbox` attribute is empty on purpose: that is the MOST restrictive
 * setting, not the least. It denies scripts, forms, popups and same-origin
 * access, so this HTML - written by a language model from pages it fetched off
 * the open web - cannot reach the session token that fetched it.
 *
 * `srcdoc` rather than the API URL directly, because an <iframe> cannot carry
 * a bearer token, so the HTML is fetched here and handed to the frame inline.
 *
 * IT WAS A BLOB URL FIRST, AND NOTHING RENDERED - a broken-document icon in
 * every browser, reported 2026-08-11. The reason is public/_headers: the
 * portal's CSP declares no `frame-src`, so it falls back to
 * `default-src 'self'` and a blob: frame is refused. `img-src` carries `blob:`
 * because AuthImage needed it; a frame source was never added when this module
 * shipped an iframe. Proved by a three-way probe on the live page - blob with
 * sandbox, blob without sandbox, srcdoc with sandbox - where only srcdoc drew
 * anything. Local verification had not caught it because a frame that never
 * loads is indistinguishable from a working sandbox by the checks used
 * (inaccessible contentDocument, unchanged document.title): both are also true
 * of nothing at all.
 *
 * What srcdoc gives this frame, verified on the live origin rather than
 * assumed: an about:srcdoc document INHERITS the embedder's CSP, so this
 * untrusted HTML runs under `default-src 'self'; script-src 'self';
 * object-src 'none'` as well as the sandbox. Inside such a frame on
 * app.snip.work a data: image loads, a remote https image is refused, and the
 * Google Fonts stylesheet still loads - exactly `img-src 'self' data: blob:`
 * and `style-src ... fonts.googleapis.com` acting on the frame's subresources.
 *
 * DO NOT "FIX" A FUTURE FRAME BY ADDING `frame-src blob:` - but not for the
 * reason the first version of this comment gave. It claimed a blob frame would
 * have carried no policy at all. THAT IS WRONG, corrected in review of #48:
 * per CSP3 documents from local schemes - about:blank, about:srcdoc, blob:,
 * data: - inherit the embedder's policy, so a blob frame would have been held
 * by this same CSP had it been allowed to load. The half that is true is
 * narrower: neither route gets the API's OWN script-free CSP, because that is
 * a response header and does not survive into a blob or an inline document.
 *
 * The real argument against widening is plainer, and survives the correction.
 * srcdoc already works, so the allowance buys nothing; `frame-src blob:` is
 * not scoped to this frame but is a standing permission for every future one,
 * granted to spare the only frame that does not need it; and it brings back
 * object-URL lifetimes to leak. A weaker true reason beats a strong false one.
 *
 * What the inherited policy costs: a draft body's remote <img> will not load
 * (`img-src 'self' data: blob:`). Today nothing renders one - neither render.py
 * nor render_email.py emits an image tag - and the Google Fonts stylesheet the
 * page does load is already allowed by `style-src`/`font-src`. If drafts ever
 * carry photographs, widen the policy deliberately and say so there, rather
 * than discovering it as a blank space in a review.
 */
function PreviewModal({ productId, draft, lang, variant, onClose }: {
  productId: string; draft: Draft; lang: string;
  variant: PreviewVariant; onClose: () => void;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    fetchText(
      `/modules/${productId}/content-studio/drafts/${draft.id}/preview/${lang}`
        + `?variant=${variant}`,
      'Could not load the preview.',
    )
      .then((value) => { if (!cancelled) setHtml(value); })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the preview.');
      });
    return () => { cancelled = true; };
  }, [productId, draft.id, lang, variant]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${draft.title} - ${lang.toUpperCase()} ${variant === 'email' ? 'email' : 'page'}`}
      width="max-w-5xl"
      fullOnMobile
    >
      {error ? <ErrorBox message={error} /> : html === null ? <Loading text="Rendering…" /> : (
        /*
          The height is "everything the modal chrome leaves", not a fraction of
          the viewport: reading is the whole job of this screen, and on a phone
          a 70vh box inside a padded dialog left an article in a letterbox.
          dvh rather than vh so iOS Safari's collapsing URL bar does not hide
          the bottom of the frame behind its own chrome.
        */
        <iframe
          title="Draft preview"
          srcDoc={html}
          sandbox=""
          className="h-[calc(100dvh-7.5rem)] w-full rounded-lg border border-paper-300 bg-white
                     dark:border-ink-700 sm:h-[75vh]"
        />
      )}
    </Modal>
  );
}

// -- decisions ----------------------------------------------------------------

/**
 * Approve is one click. Sending back and rejecting are not: both ask for a
 * reason first, and for a send-back the reason is required, because it is not
 * a comment - it is the instruction the next attempt is written against.
 */
function DecisionBar({ onDecide, busy, approveLabel = 'Approve' }: {
  onDecide: (decision: Decision, note: string) => void;
  busy: boolean;
  approveLabel?: string;
}) {
  const [asking, setAsking] = useState<Exclude<Decision, 'approve'> | null>(null);
  const [note, setNote] = useState('');

  if (asking) {
    const sendingBack = asking === 'changes';
    return (
      <div className="space-y-2 border-t border-paper-200 pt-3 dark:border-ink-700">
        <label className="block text-sm font-medium" htmlFor="decision-note">
          {sendingBack ? 'What needs to change?' : 'Why is this being dropped? (optional)'}
        </label>
        <textarea
          id="decision-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="field"
          placeholder={sendingBack
            ? 'The VAT figure in section 3 is wrong, and the comparison table needs a source.'
            : ''}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            className="btn-primary justify-center sm:justify-start"
            disabled={busy || (sendingBack && !note.trim())}
            onClick={() => onDecide(asking, note.trim())}
          >
            {busy ? <Spinner className="h-4 w-4" /> : sendingBack ? 'Send back' : 'Reject'}
          </button>
          <button
            type="button"
            className="btn-secondary justify-center sm:justify-start"
            onClick={() => setAsking(null)}
          >
            Cancel
          </button>
        </div>
        {sendingBack && !note.trim() && (
          <p className="text-xs text-ink-500 dark:text-ink-400">
            A note is required: it is what the next draft is written against.
          </p>
        )}
      </div>
    );
  }

  /*
    On a phone the three choices stack full-width - the approve button is the
    most-pressed control in the Studio and a row of three small targets put
    "Approve" a thumb-slip away from "Reject". Side by side again from `sm`.
  */
  return (
    <div className="flex flex-col gap-2 border-t border-paper-200 pt-3 dark:border-ink-700 sm:flex-row sm:flex-wrap">
      <button
        type="button"
        className="btn-primary justify-center gap-1.5 sm:justify-start"
        disabled={busy}
        onClick={() => onDecide('approve', '')}
      >
        <Check className="h-4 w-4" aria-hidden /> {approveLabel}
      </button>
      <button
        type="button"
        className="btn-secondary justify-center gap-1.5 sm:justify-start"
        disabled={busy}
        onClick={() => setAsking('changes')}
      >
        <RotateCcw className="h-4 w-4" aria-hidden /> Send back
      </button>
      <button
        type="button"
        className="btn-secondary justify-center gap-1.5 sm:justify-start"
        disabled={busy}
        onClick={() => setAsking('reject')}
      >
        <X className="h-4 w-4" aria-hidden /> Reject
      </button>
    </div>
  );
}

// -- drafts -------------------------------------------------------------------

interface FactCheck {
  state: 'checked' | 'pending';
  revision: number;
  counts: { supported: number; not_found: number; contradicted: number; unfetchable: number };
  findings: {
    id: string; claim_source: string; claim_text: string;
    source_url: string; verdict: string; evidence: string | null;
  }[];
}

/**
 * The fact check, on the card.
 *
 * The green panel above it says every source RESOLVES - that a link is not
 * dead. It says nothing about whether the article's numbers match the pages it
 * cites, which is the question a reviewer actually has. That answer already
 * existed: the runner writes a verdict per claim and the API has served them
 * since the module shipped. Nothing rendered them, so the only way to read a
 * fact check was to query the database by hand, and a reviewer looking at a
 * green tick could reasonably think the piece had been checked when the
 * findings had never even arrived (2026-08-20: an ingest was refused 422 and
 * stored nothing, and the card looked exactly the same).
 *
 * So this panel distinguishes three states the old card collapsed into one:
 * running, checked-and-clean, and checked-with-something-to-read.
 */
function FactCheckPanel({ productId, draftId }: { productId: string; draftId: string }) {
  const [data, setData] = useState<FactCheck | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const load = () => {
      get<FactCheck>(`/modules/${productId}/content-studio/drafts/${draftId}/fact-check`)
        .then((value) => {
          if (cancelled) return;
          setData(value);
          // 'pending' means no findings are stored YET. On a fresh draft the
          // check is still running in the workflow, so the panel polls rather
          // than settling on an answer that is only true for another minute.
          // It backs off to nothing once findings land, and the interval is
          // slow because a fact check takes minutes, not seconds.
          if (value.state === 'pending') timer = setTimeout(load, 20000);
        })
        .catch(() => { if (!cancelled) setFailed(true); });
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [productId, draftId]);

  // A panel that cannot load must not imply a verdict either way.
  if (failed) return null;

  if (!data || data.state === 'pending') {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-slate-100 p-3 text-sm
                      text-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
        <Spinner className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-medium">Checking the claims against their sources…</p>
          <p className="mt-1 text-xs opacity-80">
            Every figure is read back against the page it cites. This takes a few
            minutes; the panel updates itself.
          </p>
        </div>
      </div>
    );
  }

  const { supported, not_found: notFound, contradicted, unfetchable } = data.counts;
  const total = supported + notFound + contradicted + unfetchable;
  // Only a contradiction is loud. not_found is common and usually innocent - a
  // paywall, a figure quoted from a PDF - and colouring it as a problem trains
  // a reviewer to ignore the colour, which costs the contradictions their
  // warning.
  const tone = contradicted
    ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
    : 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200';

  return (
    <details className={`rounded-lg p-3 text-sm ${tone}`}>
      <summary className="flex cursor-pointer items-center gap-1.5 font-medium">
        {contradicted
          ? <><AlertTriangle className="h-4 w-4" aria-hidden />
            {contradicted} claim{contradicted === 1 ? '' : 's'} contradicted by its own source</>
          : <><Check className="h-4 w-4" aria-hidden /> Fact check passed</>}
        <span className="ml-auto text-xs font-normal opacity-70">
          {supported}/{total} supported
        </span>
      </summary>

      <p className="mt-2 text-xs opacity-80">
        {supported} supported · {notFound} not found · {contradicted} contradicted
        {unfetchable ? ` · ${unfetchable} unreadable` : ''}
        {'. '}
        &ldquo;Not found&rdquo; usually means the page did not settle it, not that
        the claim is wrong.
      </p>

      {/* Contradictions first and in full: they are the only verdict that
          should change a decision, and a reviewer needs the source's own words
          to judge whether it is a real error or the checker being literal. */}
      {data.findings.filter((f) => f.verdict === 'contradicted').map((f) => (
        <div key={f.id} className="mt-2 rounded border border-current/20 p-2 text-xs">
          <p className="font-medium">{f.claim_text}</p>
          {f.evidence ? <p className="mt-1 italic opacity-90">Source says: {f.evidence}</p> : null}
          <a
            href={f.source_url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 underline opacity-80"
          >
            <ExternalLink className="h-3 w-3" aria-hidden /> Open the source
          </a>
        </div>
      ))}
    </details>
  );
}

interface Scorecard {
  state: 'scored' | 'pending' | 'unreadable';
  revision: number;
  lang?: string;
  overall?: 'green' | 'amber' | 'red';
  scored_at?: string;
  card: {
    recommendation: string;
    limit: string;
    dimensions: {
      key: string; label: string; band: 'green' | 'amber' | 'red';
      headline: string; detail: string; evidence: string[]; judged: boolean;
    }[];
  } | null;
}

// Green, amber, red - the same three the audit playbook uses, so a card and a
// written audit can be read side by side. Amber is the one that has to look
// like "read this", not like a failure: a row that shouts at a reviewer for a
// paywalled source is a row they learn to skip.
const BAND_TONE: Record<string, string> = {
  green: 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  amber: 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  red: 'bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
};
const BAND_DOT: Record<string, string> = {
  green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-rose-500',
};

// Five minutes of waiting, then stop and say so.
//
// A DRAFT THAT WILL NEVER BE SCORED MUST NOT SPIN FOR EVER. Scoring runs once,
// at the end of the draft run that produced the revision, so a draft written
// before this feature existed has no card and never will. The first version of
// this panel polled until the tab was closed, and on 2026-08-23 a browser test
// found both live drafts stuck on "Scoring this draft..." after ten minutes.
//
// The irony is on the record: LinkedInPanel's own docstring says a spinner that
// never resolves is worse than saying plainly that nothing is there, and this
// panel was written with exactly that flaw a few hours later.
const SCORECARD_TRIES = 15;
const SCORECARD_EVERY_MS = 20000;

/**
 * The publish scorecard, on the card.
 *
 * Everything the pipeline already knew, in five rows a reviewer can read in
 * ten seconds. It exists because on 2026-08-21 an article reached the live
 * site with three embargoed figures and ten vendor blogs for a statutory
 * deadline, and every one of those signals was already computable.
 *
 * IT RECOMMENDS AND NEVER DECIDES, and the panel keeps saying so. The approve
 * button is not disabled by a red row and never should be: on that same
 * article the fact check reported two contradictions and both were false.
 */
function ScorecardPanel({ productId, draftId }: { productId: string; draftId: string }) {
  const [data, setData] = useState<Scorecard | null>(null);
  const [failed, setFailed] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let tries = 0;
    // Belt and braces with the list's key: if this panel is ever reused for a
    // different draft rather than remounted, a stale "not scored" would be the
    // first thing shown for a draft still loading.
    setGaveUp(false);

    const load = () => {
      get<Scorecard>(`/modules/${productId}/content-studio/drafts/${draftId}/scorecard`)
        .then((value) => {
          if (cancelled) return;
          setData(value);
          // Scoring is the last step of the draft run, so on a FRESH draft the
          // card is still to come and polling is right. On an old one it is
          // not coming at all, which is what the ceiling is for.
          if (value.state !== 'pending') return;
          tries += 1;
          if (tries >= SCORECARD_TRIES) { setGaveUp(true); return; }
          timer = setTimeout(load, SCORECARD_EVERY_MS);
        })
        .catch(() => { if (!cancelled) setFailed(true); });
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [productId, draftId]);

  // A panel that cannot load must not imply a verdict either way.
  if (failed || !data) return null;

  if (data.state === 'pending') {
    // Waited long enough. An honest absence beats a spinner that is still
    // spinning tomorrow, and this is the likely state for every draft written
    // before scoring existed.
    if (gaveUp) {
      return (
        <div className="rounded-lg bg-slate-100 p-3 text-sm text-slate-700
                        dark:bg-slate-800/60 dark:text-slate-300">
          <p className="font-medium">This revision was not scored.</p>
          <p className="mt-1 text-xs opacity-80">
            Scoring runs once, at the end of the run that writes a revision, so a
            draft from before it existed has no card and will not get one. Judge
            this draft on the source check and the fact check above.
          </p>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-2 rounded-lg bg-slate-100 p-3 text-sm
                      text-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
        <Spinner className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-medium">Scoring this draft…</p>
          <p className="mt-1 text-xs opacity-80">
            Sourcing, claims, house rules, structure and who the piece is written
            for. The panel updates itself.
          </p>
        </div>
      </div>
    );
  }

  // An unreadable row must not render as an empty card, because an empty card
  // looks exactly like a clean one.
  if (data.state === 'unreadable' || !data.card) {
    return (
      <div className="rounded-lg bg-slate-100 p-3 text-sm text-slate-700
                      dark:bg-slate-800/60 dark:text-slate-300">
        <p className="font-medium">The scorecard for this revision could not be read.</p>
        <p className="mt-1 text-xs opacity-80">Judge the draft on its own terms.</p>
      </div>
    );
  }

  const overall = data.overall ?? 'amber';

  return (
    <details className={`rounded-lg p-3 text-sm ${BAND_TONE[overall]}`} open={overall === 'red'}>
      <summary className="flex cursor-pointer items-center gap-1.5 font-medium">
        <Gauge className="h-4 w-4" aria-hidden />
        {data.card.recommendation}
      </summary>

      <ul className="mt-2 space-y-1.5">
        {data.card.dimensions.map((d) => (
          <li key={d.key} className="rounded border border-current/20 p-2 text-xs">
            <p className="flex items-center gap-1.5 font-medium">
              <span className={`h-2 w-2 shrink-0 rounded-full ${BAND_DOT[d.band]}`}
                    aria-hidden />
              {d.label}
              {/* A judged row is a model's opinion. Labelling it is the whole
                  difference between a reading and a measurement. */}
              {d.judged ? (
                <span className="rounded bg-current/10 px-1 py-px text-[10px] uppercase
                                 tracking-wide opacity-70">judged</span>
              ) : null}
              <span className="ml-auto font-normal opacity-70">{d.headline}</span>
            </p>
            {d.detail ? <p className="mt-1 opacity-90">{d.detail}</p> : null}
            {d.evidence.length ? (
              <ul className="mt-1 list-disc space-y-0.5 pl-4 opacity-80">
                {d.evidence.map((e) => <li key={e}>{e}</li>)}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>

      <p className="mt-2 text-xs opacity-70">{data.card.limit}</p>
    </details>
  );
}

function DraftCard({ productId, draft, subscribers, onDone }: {
  productId: string;
  draft: Draft;
  subscribers: AudienceCount[];
  onDone: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ lang: string; variant: PreviewVariant } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: Decision, note: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ publish?: { published: boolean; detail: string } }>(
        `/modules/${productId}/content-studio/drafts/${draft.id}/decision`,
        { decision, note: note || undefined },
      );
      // An approval that was recorded and a publish that failed are two
      // different things, and the reader has to be told which happened.
      onDone(result.publish ? result.publish.detail : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The decision did not go through.');
    } finally {
      setBusy(false);
    }
  };

  const sources = draft.sources ?? [];
  const isIssue = draft.channel === 'newsletter';
  // Only the languages this issue actually has can receive it.
  const reach = isIssue
    ? subscribers
      .filter((s) => draft.langs.some((l) => l.lang === s.lang))
      .reduce((total, s) => total + s.n, 0)
    : 0;

  return (
    <div className="card space-y-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{draft.title}</h3>
          <p className="text-xs text-ink-500 dark:text-ink-400">
            Revision {draft.revision} · drafted {formatDateTime(draft.created_at)}
            {draft.updated_at && draft.updated_at !== draft.created_at
              ? ` · last change ${formatDateTime(draft.updated_at)}`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isIssue && (
            <span className="badge bg-brand-500 text-white">Newsletter</span>
          )}
          <span className="badge bg-paper-200 text-ink-600 dark:bg-ink-700 dark:text-paper-300">
            {draft.stage ? STAGE_LABEL[draft.stage] ?? draft.stage : draft.status}
          </span>
        </div>
      </div>

      {/* Publishing a post can be undone by deleting a page. Sending cannot be
          undone at all, so the number of people it reaches belongs next to the
          button, not on another screen. */}
      {isIssue && draft.can_decide && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm
                        text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <Send className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            {reach > 0 ? (
              <>
                Approving sends this to <strong>{formatNumber(reach)} subscriber
                {reach === 1 ? '' : 's'}</strong>. It cannot be unsent.
              </>
            ) : (
              <>
                There are no confirmed subscribers for this issue&apos;s
                language{draft.langs.length === 1 ? '' : 's'} yet, so approving
                will not send anything.
              </>
            )}
          </p>
        </div>
      )}

      {/* The machine's verdict, with its limit stated. verify.py can prove a
          source URL resolves; only the reader can prove the figure is right. */}
      <div className={`rounded-lg p-3 text-sm ${draft.verify_passed
        ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'
        : 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'}`}
      >
        <p className="flex items-center gap-1.5 font-medium">
          {draft.verify_passed
            ? <><Check className="h-4 w-4" aria-hidden /> Every source resolves</>
            : <><AlertTriangle className="h-4 w-4" aria-hidden /> Source check did not pass</>}
        </p>
        <p className="mt-1 text-xs opacity-80">
          This checks that the links work, not that the figures match what they say.
          Read the piece before approving it.
        </p>
      </div>

      {/* And the half the panel above cannot answer: whether the numbers match. */}
      <FactCheckPanel productId={productId} draftId={draft.id} />

      {/* Everything else the pipeline knew, in one place: sourcing, house
          rules, structure and who the piece is actually written for. */}
      <ScorecardPanel productId={productId} draftId={draft.id} />

      {/* Reading is the step every decision depends on, so on a phone each
          language is a full-width press rather than a small chip. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {draft.langs.map((l) => (
          <button
            key={l.lang}
            type="button"
            className="btn-secondary justify-center gap-1.5 sm:justify-start"
            // An issue opens as the EMAIL, because that is the artefact being
            // approved. Reviewing the site-styled page and then sending
            // something else is the review gate looking at the wrong thing.
            onClick={() => setPreview({ lang: l.lang, variant: isIssue && l.has_email ? 'email' : 'page' })}
            disabled={!l.has_page && !l.has_email}
          >
            <Eye className="h-4 w-4" aria-hidden />
            Read {l.lang.toUpperCase()}
            {isIssue && l.has_email ? <span className="text-xs opacity-70">email</span> : null}
            {l.word_count ? (
              <span className="text-xs opacity-70">{formatNumber(l.word_count)} words</span>
            ) : null}
          </button>
        ))}
      </div>

      {sources.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium">
            {sources.length} source{sources.length === 1 ? '' : 's'}
          </summary>
          <ul className="mt-2 space-y-1">
            {sources.map((s, i) => (
              <li key={s.url ?? i} className="text-ink-600 dark:text-paper-300">
                <span className="font-medium">{s.publisher ?? 'Source'}</span>
                {s.title ? ` — ${s.title}` : ''}
                {s.url && (
                  <>
                    {' '}
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-brand-600 underline dark:text-brand-400"
                    >
                      link
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {error && <ErrorBox message={error} />}

      {draft.can_decide ? (
        <DecisionBar
          onDecide={decide}
          busy={busy}
          approveLabel={isIssue ? (reach > 0 ? 'Approve and send' : 'Approve') : 'Approve'}
        />
      ) : (
        <p className="border-t border-paper-200 pt-3 text-sm text-ink-500 dark:border-ink-700 dark:text-ink-400">
          {draft.stage === 'internal'
            ? 'With snip.work for review. It comes to you once they have signed it off.'
            : 'Waiting on the other reviewer.'}
        </p>
      )}

      {preview && (
        <PreviewModal
          productId={productId}
          draft={draft}
          lang={preview.lang}
          variant={preview.variant}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/**
 * A draft that was approved and then did not make it.
 *
 * This exists because of a bug found while testing: a send that could not start
 * left the issue at 'publishing' with the reason on the row, and the Drafts tab
 * - which listed only items awaiting review - stopped showing it entirely.
 * Dismiss the toast and a half-delivered issue was invisible. Something that
 * looks finished and is not is the exact failure this whole feature exists to
 * prevent, so it gets its own section and a retry.
 */
function StalledCard({ productId, draft, onDone }: {
  productId: string; draft: Draft; onDone: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isIssue = draft.channel === 'newsletter';

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ detail?: string }>(
        `/modules/${productId}/content-studio/drafts/${draft.id}/publish`, {});
      onDone(result.detail ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card space-y-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{draft.title}</h3>
          <p className="text-xs text-ink-500 dark:text-ink-400">
            Approved · {isIssue ? 'sending' : 'publishing'} did not finish
          </p>
        </div>
        <span className="badge bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300">
          {draft.status === 'publishing' ? (isIssue ? 'Part sent' : 'Publishing') : draft.status}
        </span>
      </div>

      {draft.error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/40 dark:text-red-200">
          {draft.error}
        </p>
      )}
      <p className="text-sm text-ink-600 dark:text-paper-300">
        {isIssue
          ? 'Anyone already sent to will not be sent to again. Retrying continues from where it stopped.'
          : 'The approval still stands. Retrying re-runs publishing only.'}
      </p>

      {error && <ErrorBox message={error} />}
      <button
        type="button"
        className="btn-primary inline-flex items-center gap-1.5"
        disabled={busy}
        onClick={retry}
      >
        {busy ? <Spinner className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" aria-hidden />}
        Try again
      </button>
    </div>
  );
}

// -- topics -------------------------------------------------------------------

/**
 * Ask for more topics.
 *
 * Deliberately not on the Drafts tab: research proposes SUBJECTS, and the
 * place a person notices they have run out of them is the list of subjects.
 *
 * It does not wait for the run. A research pass takes minutes - a spinner held
 * that long would either be abandoned or read as a hang - so the click reports
 * that the run started and Activity carries the rest.
 */
function FindTopicsButton({ productId, onDone }: {
  productId: string; onDone: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One link, optional. Deliberately not a brief: the ask was to point a round
  // at something worth reading, and a box that invites a paragraph invites
  // prompt-writing, which is a different job with different failure modes.
  const [seedUrl, setSeedUrl] = useState('');

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ started: boolean; detail: string }>(
        `/modules/${productId}/content-studio/research`,
        seedUrl.trim() ? { seed_url: seedUrl.trim() } : {});
      if (result.started) { setSeedUrl(''); onDone(result.detail); }
      else setError(result.detail);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The run did not start.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="url"
          className="field max-w-md"
          placeholder="Optional: a link to build this round around"
          value={seedUrl}
          onChange={(e) => setSeedUrl(e.target.value)}
          disabled={busy}
          aria-label="A link to build this research round around"
        />
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-1.5"
          disabled={busy}
          onClick={start}
        >
          <Search className="h-4 w-4" aria-hidden />
          {busy ? 'Starting…' : 'Find new topics'}
        </button>
      </div>
      {error && <ErrorBox message={error} />}
    </div>
  );
}

/**
 * Add a topic by hand.
 *
 * Until this existed, a subject could only enter the queue if a research run
 * thought of it - which for an agency's blog is backwards. The best topics come
 * from what a client asked twice this week, and there was nowhere to put one.
 *
 * It arrives 'approved', because the person adding it is the person who would
 * have approved it.
 */
function AddTopicForm({ productId, onDone }: {
  productId: string; onDone: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [angle, setAngle] = useState('');
  const [sources, setSources] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await post(`/modules/${productId}/content-studio/topics`, {
        title: title.trim(),
        angle: angle.trim() || undefined,
        // One per line is what a person pasting links actually does.
        sources: sources.split(/\s+/).map((u) => u.trim()).filter(Boolean),
        valid_until: validUntil || undefined,
      });
      setTitle(''); setAngle(''); setSources(''); setValidUntil(''); setOpen(false);
      onDone('Topic added, ready to draft.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that topic.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn-secondary inline-flex items-center gap-1.5"
        onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" aria-hidden />
        Add a topic
      </button>
    );
  }

  return (
    <div className="card space-y-3 p-4 sm:p-5">
      <h3 className="font-semibold">Add a topic</h3>
      <div className="space-y-3">
        <div>
          <label className="form-label" htmlFor="new-topic-title">Title</label>
          <input
            id="new-topic-title" className="field" value={title} disabled={busy}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What should the article be called?"
          />
        </div>
        <div>
          <label className="form-label" htmlFor="new-topic-angle">Angle (optional)</label>
          <input
            id="new-topic-angle" className="field" value={angle} disabled={busy}
            onChange={(e) => setAngle(e.target.value)}
            placeholder="One line on the specific take"
          />
        </div>
        <div>
          <label className="form-label" htmlFor="new-topic-sources">
            Links (optional, one per line)
          </label>
          <textarea
            id="new-topic-sources" className="field" rows={2} value={sources}
            disabled={busy} onChange={(e) => setSources(e.target.value)}
            placeholder="https://…"
          />
        </div>
        <div>
          <label className="form-label" htmlFor="new-topic-valid-until">
            Useful until (optional)
          </label>
          {/*
            The deadline, when the subject has one - an event, a funding
            window, a rule change. The runner will not draft it after this
            date and the publish gate will refuse it, so a dated subject
            entered here can never quietly ship after its own point.
          */}
          <input
            id="new-topic-valid-until" className="field" type="date"
            value={validUntil} disabled={busy}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </div>
      </div>
      {error && <ErrorBox message={error} />}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button type="button" className="btn-primary justify-center sm:justify-start"
          disabled={busy || !title.trim()} onClick={submit}>
          {busy ? 'Adding…' : 'Add topic'}
        </button>
        <button type="button" className="btn-secondary justify-center sm:justify-start" disabled={busy}
          onClick={() => { setOpen(false); setError(null); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * The research step's own judgement of the topic, on the card where the human
 * makes theirs. Three 1-5 scores and, when the subject has one, its shelf
 * life. Unscored topics (everything proposed before migration 0018, and most
 * hand-added ones) show nothing rather than a row of dashes - absence of a
 * score is not information worth a line of screen.
 *
 * The deadline changes colour as it approaches because that is the decision it
 * exists to drive: an amber date says "approve this one first", a red one says
 * "this piece died in the queue - archive it or re-date it".
 */
function TopicScores({ topic }: { topic: Topic }) {
  const scores: [string, number | null | undefined][] = [
    ['Relevance', topic.relevance],
    ['Urgency', topic.urgency],
    ['Evidence', topic.evidence],
  ];
  const scored = scores.filter(([, value]) => value != null) as [string, number][];

  let deadline: { text: string; tone: string } | null = null;
  if (topic.valid_until) {
    const days = Math.floor(
      (Date.parse(topic.valid_until) - Date.now()) / 86_400_000);
    deadline = days < 0
      ? { text: `Expired ${topic.valid_until}`,
          tone: 'text-red-700 dark:text-red-400 font-medium' }
      : days <= 14
        ? { text: `Useful until ${topic.valid_until} (${days}d)`,
            tone: 'text-amber-700 dark:text-amber-400 font-medium' }
        : { text: `Useful until ${topic.valid_until}`,
            tone: 'text-ink-500 dark:text-ink-400' };
  }

  if (!scored.length && !deadline) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500 dark:text-ink-400">
      {scored.map(([label, value]) => (
        <span key={label}>
          <span className="font-medium">{label}</span> {value}/5
        </span>
      ))}
      {deadline && <span className={deadline.tone}>{deadline.text}</span>}
    </p>
  );
}

function TopicCard({ productId, topic, canDraft, onDone, onNotice }: {
  productId: string; topic: Topic; canDraft: boolean;
  onDone: () => void; onNotice: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Dispatched, but the runner has not claimed it yet.
   *
   * The App starts a workflow; GitHub queues it; the runner claims the topic
   * some seconds later. Only that last step writes 'drafting'. Without this the
   * card sits unchanged through the whole gap and the button invites a second
   * click, which would dispatch a second run for the same subject.
   *
   * It expires rather than waiting for a contradiction: if the run died before
   * claiming - the failure mode of every draft run this morning - no state
   * change is ever coming, and a card stuck on "Starting" would be its own
   * small lie. Sixty seconds is far longer than the gap and far shorter than a
   * run.
   */
  const [justStarted, setJustStarted] = useState(false);
  useEffect(() => {
    if (!justStarted) return undefined;
    const timer = setTimeout(() => setJustStarted(false), 60_000);
    return () => clearTimeout(timer);
  }, [justStarted]);

  const decide = async (decision: Decision) => {
    setBusy(true);
    setError(null);
    try {
      await post(`/modules/${productId}/content-studio/topics/${topic.id}/decision`, { decision });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The decision did not go through.');
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    setBusy(true);
    setError(null);
    try {
      await post(`/modules/${productId}/content-studio/topics/${topic.id}/release`, {});
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'It could not be put back.');
    } finally {
      setBusy(false);
    }
  };

  const draftNow = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ started: boolean; detail: string }>(
        `/modules/${productId}/content-studio/topics/${topic.id}/draft`, {});
      // A dispatch that GitHub refused comes back 200 with started:false, the
      // run row already recorded as failed. Reporting it as a notice rather
      // than throwing keeps the Activity row and the message consistent.
      if (result.started) { setJustStarted(true); onNotice(result.detail); }
      else setError(result.detail);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The run did not start.');
    } finally {
      setBusy(false);
    }
  };

  const decided = topic.status !== 'proposed';
  const badge = TOPIC_STATE[topic.status] ?? { label: topic.status, chip: NEUTRAL_CHIP };
  // The server's view, and the ten to twenty seconds before it has one.
  const claimed = topic.status === 'drafting' && !topic.stalled;
  const working = claimed || justStarted;

  return (
    <div className="card space-y-2 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{topic.title}</h3>
          {/*
            Last change to the minute, titled with the UTC instant. A topic
            claimed, released and re-claimed inside an hour is three identical
            rows without it, which is how six topics read as four.
          */}
          <p
            className="text-xs text-ink-500 dark:text-ink-400"
            title={`${topic.updated_at ?? topic.created_at} (UTC)`}
          >
            Last change {formatDateTime(topic.updated_at ?? topic.created_at)}
          </p>
        </div>
        {working ? (
          <Working label={claimed ? 'Being drafted now' : 'Starting the run…'} />
        ) : decided && (
          <span className={`badge ${topic.stalled ? STALLED_CHIP : badge.chip}`}>
            {topic.stalled ? 'Started drafting, then stopped' : badge.label}
          </span>
        )}
      </div>
      {/*
        A claimed topic that never finished. The run marked it 'drafting' so a
        second run could not take the same subject, then died before handing it
        back - and until it is handed back nothing can draft it, because the
        claim only takes 'approved'.

        The pipeline releases its own claim now, so this is the backstop for a
        runner that vanished without reaching its cleanup step. It stays a
        button rather than an automatic sweep because the run may still be
        alive: releasing a topic under a working run would let a second one
        claim it and write the same article twice.
      */}
      {topic.stalled && canDraft && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm dark:bg-amber-950/40">
          <p className="text-ink-600 dark:text-paper-300">
            A run claimed this {formatDateTime(topic.updated_at ?? topic.created_at)} and
            never finished. Nothing can draft it until it is put back.
          </p>
          {/*
            TWO WAYS OUT, not one. Until 2026-08-22 the only control here was
            "put it back", which returns the topic to 'approved' - the one
            status a run can claim. Declining a stalled topic therefore meant
            re-arming it and racing the scheduler, so a careful reviewer asked
            to decline one would correctly refuse to touch this card at all.
            Rejecting goes straight to 'rejected' with no window in between.
          */}
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              className="btn-secondary justify-center gap-1.5 sm:justify-start"
              disabled={busy}
              onClick={release}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              {busy ? 'Putting it back…' : 'Put it back in the queue'}
            </button>
            <button
              type="button"
              className="btn-secondary justify-center gap-1.5 sm:justify-start"
              disabled={busy}
              onClick={() => decide('reject')}
            >
              <X className="h-4 w-4" aria-hidden /> Not this one
            </button>
          </div>
        </div>
      )}
      {/*
        A refresh is the published article coming back for its six-monthly
        update, not a new subject: approving it rewrites the live piece at the
        same URL, and declining it leaves the article exactly as it is. The
        card has to say so, or "Not this one" reads like deleting the article.
      */}
      {topic.refresh_requested_at && (
        <p className="rounded-md bg-sky-50 px-2.5 py-1.5 text-sm text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
          <span className="font-medium">Refresh of a published article.</span>{' '}
          Approving rewrites it in place at the same URL; declining leaves it as it is.
          {topic.refresh_reason && <> Why now: {topic.refresh_reason}</>}
        </p>
      )}
      {topic.angle && <p className="text-sm text-ink-600 dark:text-paper-300">{topic.angle}</p>}
      {topic.rationale && !topic.refresh_requested_at && (
        <p className="text-sm text-ink-500 dark:text-ink-400">
          <span className="font-medium">Why now:</span> {topic.rationale}
        </p>
      )}
      <TopicScores topic={topic} />

      {/*
        The research's sources, as domains, on the card where the approval
        happens. The funding article retracted on 2026-08-15 was researched
        entirely from UK government domains - visible here, that topic dies
        as a proposal in one glance instead of costing a research run, a
        draft run, a review, a publish and a retraction.
      */}
      {(topic.sources?.length ?? 0) > 0 && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500 dark:text-ink-400">
          <span className="font-medium">Researched from:</span>
          {topic.sources!.map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              title={url}
              className="inline-flex items-center gap-1 underline hover:text-ink-700 dark:hover:text-paper-200"
            >
              {sourceHost(url)}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          ))}
        </p>
      )}
      {error && <ErrorBox message={error} />}
      {!decided && (
        <div className="flex flex-col gap-2 pt-1 sm:flex-row">
          <button
            type="button"
            className="btn-primary justify-center gap-1.5 sm:justify-start"
            disabled={busy}
            onClick={() => decide('approve')}
          >
            <Check className="h-4 w-4" aria-hidden /> Approve
          </button>
          <button
            type="button"
            className="btn-secondary justify-center gap-1.5 sm:justify-start"
            disabled={busy}
            onClick={() => decide('reject')}
          >
            <X className="h-4 w-4" aria-hidden /> Not this one
          </button>
        </div>
      )}
      {/*
        Approved topics wait for the schedule. This is the way to say "that one,
        now" - which is what an approval usually means when somebody has just
        read the subject and wants the piece.

        Only on `approved`: a topic already 'drafting' or 'done' would either
        collide with a run in flight or rewrite something finished, and the
        claim endpoint refuses both anyway. The button is not offered rather
        than offered and rejected.
      */}
      {topic.status === 'approved' && !working && (
        <div className="flex flex-col gap-2 pt-1 sm:flex-row">
          {canDraft && (
            <button
              type="button"
              className="btn-secondary justify-center gap-1.5 sm:justify-start"
              disabled={busy}
              onClick={draftNow}
            >
              <PenLine className="h-4 w-4" aria-hidden />
              {busy ? 'Starting…' : 'Draft this now'}
            </button>
          )}
          {/*
            Approval is not a one-way door. Without this the only ways to take
            an approved topic off the list were to draft it - minutes of model
            time to say no - or to ask for a database edit. The API refuses it
            while a run holds the topic, so the button hides then too (`working`
            covers it).
          */}
          <button
            type="button"
            className="btn-secondary justify-center gap-1.5 sm:justify-start"
            disabled={busy}
            onClick={() => decide('reject')}
          >
            <X className="h-4 w-4" aria-hidden /> Not this one
          </button>
        </div>
      )}
    </div>
  );
}

// -- runs ---------------------------------------------------------------------

/**
 * What the automation did. A pipeline that has stopped running looks exactly
 * like a pipeline with nothing to do - no topics appear either way - so the
 * failures have to be on the same screen as the work.
 */
function Runs({ runs }: { runs: Run[] }) {
  if (!runs.length) {
    return (
      <div className="card">
        <Empty
          title="Nothing has run yet"
          description="Research and drafting report in here once the pipeline is switched on."
        />
      </div>
    );
  }
  return (
    <div className="card divide-y divide-paper-200 dark:divide-ink-700">
      {runs.map((run) => (
        <div key={run.id} className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
          <div>
            <span className="font-medium capitalize">{run.kind}</span>
            {run.ref && <span className="ml-2 text-ink-500 dark:text-ink-400">{run.ref}</span>}
            {run.status === 'failed' && run.detail?.error ? (
              <p className="mt-0.5 text-xs text-red-700 dark:text-red-400">
                {String(run.detail.error)}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            {/*
              To the second, and titled with the raw UTC instant. Diagnosing a
              run means lining this row up against a GitHub Actions log, which
              is stamped in UTC while this reads in the viewer's timezone.
              The duration is the other half: it separates a dispatch refused
              in a second from a model that burned five minutes and returned
              nothing.
            */}
            <span
              className="text-xs text-ink-500 dark:text-ink-400"
              title={`${run.started_at} (UTC)`}
            >
              {formatTimestamp(run.started_at)}
              {run.finished_at && formatDuration(run.started_at, run.finished_at)
                ? ` · ${formatDuration(run.started_at, run.finished_at)}`
                : ''}
            </span>
            <span className={`badge ${
              run.status === 'ok'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                : run.status === 'failed'
                  ? 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                  : 'bg-paper-200 text-ink-600 dark:bg-ink-700 dark:text-paper-300'}`}
            >
              {run.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// -- the published page -------------------------------------------------------

interface PublishedLang { lang: string; title: string; published_url: string | null }

interface PublishedArticle {
  id: string; revision: number; status: string; visibility: string;
  error: string | null; published_at: string | null; updated_at: string;
  title: string; slug: string; langs: PublishedLang[];
}

/**
 * What the pages of a published article are doing right now, said the way a
 * reader would say it. The three pending states render as Working - a Hide
 * that has been dispatched but not deployed is neither live nor hidden, and
 * drawing either would be the page lying about the site.
 */
const VISIBILITY_STATE: Record<string, { label: string; chip: string; pending?: boolean }> = {
  live: {
    label: 'Live',
    chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
  },
  hiding: { label: 'Hiding', chip: '', pending: true },
  hidden: { label: 'Hidden', chip: NEUTRAL_CHIP },
  restoring: { label: 'Restoring', chip: '', pending: true },
  deleting: { label: 'Deleting', chip: '', pending: true },
  deleted: { label: 'Deleted', chip: NEUTRAL_CHIP },
};

interface LinkedInPosts {
  state: 'drafted' | 'pending';
  revision: number;
  posts: {
    lang: string; body: string; image_path: string;
    status: 'drafted' | 'approved' | 'declined';
    decided_at: string | null; decided_by: string | null; note: string | null;
  }[];
}

/**
 * The LinkedIn post, on the published article it belongs to.
 *
 * linkedin.py has drafted a post per language since the first article and
 * wrote them only to marketing/linkedin/<slug>.md in the content repository.
 * That is the right place for the pipeline to keep them and the wrong place
 * for a person to read them: publishing here and then opening GitHub to find
 * the post that goes with it is the seam where a post quietly never gets
 * written, which is the failure the drafting step exists to prevent.
 *
 * Deliberately NOT a spinner-and-poll like the fact check. By the time an
 * article is on this tab the drafting has already run - so no post means it
 * failed or was skipped, and saying so plainly with the way to fix it beats a
 * spinner that never resolves.
 */
function LinkedInPanel({
  productId, article,
}: { productId: string; article: PublishedArticle }) {
  const { ask } = useAsk();
  const [data, setData] = useState<LinkedInPosts | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which languages' card images failed to load. A card that does not appear
  // has to SAY it did not appear: on 2026-08-23 both were blocked by the
  // portal's own CSP and the panel collapsed the <img> to nothing, leaving a
  // bare "Attach this image to the post" link that reads like a design choice.
  const [brokenImages, setBrokenImages] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setBrokenImages([]);      // a different article's failures are not this one's
    get<LinkedInPosts>(`/modules/${productId}/content-studio/drafts/${article.id}/linkedin`)
      .then((value) => { if (!cancelled) setData(value); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [productId, article.id]);

  // The card is stored as a site-relative path because the origin belongs to
  // the product, not to the row. Recover it from a published URL rather than
  // hardcoding a domain that is right for exactly one tenant.
  const origin = (() => {
    const url = article.langs.find((l) => l.published_url)?.published_url;
    try { return url ? new URL(url).origin : ''; } catch { return ''; }
  })();

  // Declining asks for the reason, because the note is what tells the next
  // redraft what to fix. Approving does not: making someone type to say "this
  // is fine" is how a review step becomes a thing people skip.
  const decide = async (lang: string, decision: 'approve' | 'reject') => {
    const note = decision === 'reject'
      ? await ask({
        title: `Decline the ${lang.toUpperCase()} post?`,
        field: { label: 'What is wrong with it', required: true, multiline: true },
        confirmLabel: 'Decline',
        danger: true,
      })
      : null;
    if (decision === 'reject' && !note) return;
    setDeciding(lang);
    setError(null);
    try {
      await post(
        `/modules/${productId}/content-studio/drafts/${article.id}/linkedin/${lang}/decision`,
        { decision, note },
      );
      const fresh = await get<LinkedInPosts>(
        `/modules/${productId}/content-studio/drafts/${article.id}/linkedin`);
      setData(fresh);
    } catch (e) {
      // Every other handler on this screen surfaces its failure, and this one
      // did not: a refused decision left the badge unchanged with nothing
      // saying why, which reads exactly like a decision that was recorded.
      setError(e instanceof Error ? e.message : 'The decision did not go through.');
    } finally {
      setDeciding(null);
    }
  };

  const copy = (lang: string, body: string) => {
    navigator.clipboard?.writeText(body).then(
      () => { setCopied(lang); setTimeout(() => setCopied(null), 2000); },
      () => setCopied(null),
    );
  };

  if (failed || !data) return null;

  if (data.state === 'pending') {
    return (
      <p className="rounded-lg bg-slate-100 p-3 text-sm text-slate-700
                    dark:bg-slate-800/60 dark:text-slate-300">
        No LinkedIn post was drafted for this revision. Drafting is advisory at
        publish time, so a model outage skips it without holding the article
        back. Re-run the <span className="font-mono text-xs">linkedin</span>{' '}
        workflow for <span className="font-mono text-xs">{article.slug}</span>{' '}
        to draft it now.
      </p>
    );
  }

  const cleared = data.posts.filter((p) => p.status === 'approved').length;

  return (
    <div className="space-y-3">
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide
                    text-ink-500 dark:text-ink-400">
        LinkedIn
        {/* The count, not just per-post badges: with two languages it is the
            difference between "have I done this" and having to read both. */}
        <span className="normal-case tracking-normal">
          {cleared} of {data.posts.length} approved
        </span>
      </p>
      {error && <ErrorBox message={error} />}
      {data.posts.map((post) => (
        <div key={post.lang}
             className="rounded-lg border border-ink-200 p-3 dark:border-ink-700">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase">{post.lang}</span>

            {/* Three states, and 'drafted' is deliberately loud rather than
                neutral. An unreviewed post that looks calm is one that gets
                posted unread, which is the whole thing this exists to stop. */}
            <span className={`badge text-xs ${
              post.status === 'approved'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                : post.status === 'declined'
                  ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'}`}>
              {post.status === 'approved' ? 'Approved to post'
                : post.status === 'declined' ? 'Not this one'
                  : 'Not reviewed yet'}
            </span>

            <button type="button" className="btn-secondary ml-auto text-xs"
                    onClick={() => copy(post.lang, post.body)}>
              {copied === post.lang
                ? <><Check className="h-3.5 w-3.5" aria-hidden /> Copied</>
                : <><Copy className="h-3.5 w-3.5" aria-hidden /> Copy post</>}
            </button>
          </div>

          {post.decided_at && (
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              {post.status === 'approved' ? 'Approved' : 'Declined'}{' '}
              {formatDateTime(post.decided_at)}
              {post.decided_by ? ` by ${post.decided_by}` : ''}
              {post.note ? ` - ${post.note}` : ''}
            </p>
          )}

          {origin && (
            // The image is not decoration here: the house rule is that the post
            // carries it, so it is shown at the size it will be seen and links
            // out for the reviewer to save.
            <a href={`${origin}${post.image_path}`} target="_blank" rel="noreferrer"
               className="mt-2 block">
              {brokenImages.includes(post.lang) ? (
                <span className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50
                                 p-2 text-xs text-amber-900
                                 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    The card did not load here. Open it in a new tab to check it before
                    you attach it: <span className="break-all font-mono">{origin}{post.image_path}</span>
                  </span>
                </span>
              ) : (
                <img src={`${origin}${post.image_path}`} alt=""
                     onError={() => setBrokenImages(
                       (was) => (was.includes(post.lang) ? was : [...was, post.lang]))}
                     className="w-full rounded border border-ink-200 dark:border-ink-700" />
              )}
              <span className="mt-1 flex items-center gap-1 text-xs text-ink-500 dark:text-ink-400">
                <ImageIcon className="h-3.5 w-3.5" aria-hidden />
                Attach this image to the post
                <ExternalLink className="h-3 w-3" aria-hidden />
              </span>
            </a>
          )}

          <p className="mt-2 whitespace-pre-wrap text-sm">{post.body}</p>

          {/* One decision per language, because they are two posts. On the
              first article the English one was clean and the Portuguese one
              carried a banned em-dash; a single approval would have cleared
              the broken one.

              Offered whatever the current state: a decision made in haste has
              to be reversible, and a redraft resets it anyway. */}
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              className="btn-primary justify-center gap-1.5 text-xs sm:justify-start"
              disabled={deciding === post.lang || post.status === 'approved'}
              onClick={() => decide(post.lang, 'approve')}
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
              {deciding === post.lang ? 'Saving…' : 'Approve this post'}
            </button>
            <button
              type="button"
              className="btn-secondary justify-center gap-1.5 text-xs sm:justify-start"
              disabled={deciding === post.lang}
              onClick={() => decide(post.lang, 'reject')}
            >
              <X className="h-3.5 w-3.5" aria-hidden /> Not this one
            </button>
          </div>
        </div>
      ))}

      {/* The App knows which posts are cleared; it cannot know whether one was
          posted. Saying so beats implying a completeness it does not have. */}
      <p className="text-xs text-ink-500 dark:text-ink-400">
        Approving records that you have read the post. Nothing posts to LinkedIn
        automatically - you still copy the text and attach the card yourself.
      </p>
    </div>
  );
}

function PublishedCard({
  productId, article, onDone,
}: {
  productId: string; article: PublishedArticle; onDone: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const act = (action: 'hide' | 'delete' | 'restore') => {
    setBusy(true);
    post<{ visibility: string; pending: boolean; detail: string }>(
      `/modules/${productId}/content-studio/drafts/${article.id}/visibility`, { action },
    )
      .then((r) => onDone(r.detail))
      .catch((e) => onDone(e instanceof Error ? e.message : 'That did not work.'))
      .finally(() => { setBusy(false); setConfirming(false); });
  };

  const state = VISIBILITY_STATE[article.visibility]
    ?? { label: article.visibility, chip: NEUTRAL_CHIP };
  const deleted = article.visibility === 'deleted';

  return (
    <div className={`card space-y-3 p-4 sm:p-5 ${deleted ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{article.title}</h3>
          <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
            {article.published_at ? formatDateTime(article.published_at) : '—'}
            {article.revision > 1 && ` · revision ${article.revision}`}
          </p>
        </div>
        {state.pending
          ? <Working label={state.label} />
          : <span className={`badge ${state.chip}`}>{state.label}</span>}
      </div>

      {/* One link per language, to the page a reader would actually get.
          Hidden and deleted articles keep the URLs drawn but inert - the
          reader should see WHERE it was without being handed a link that 404s. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {article.langs.map((l) => (
          <span key={l.lang} className="inline-flex items-center gap-1">
            <span className="uppercase text-xs text-ink-500 dark:text-ink-400">{l.lang}</span>
            {l.published_url && article.visibility === 'live' ? (
              <a
                href={l.published_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline"
              >
                {l.title}
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            ) : (
              <span>{l.title}</span>
            )}
          </span>
        ))}
      </div>

      {article.error && (
        <p className="rounded-lg bg-red-50 p-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300">
          {article.error}
        </p>
      )}

      {!deleted && <LinkedInPanel productId={productId} article={article} />}

      {!state.pending && !deleted && (
        confirming ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>
              Delete takes it off the site for good. It stays archived here, but
              there is no restore.
            </span>
            <button type="button" className="btn-danger" disabled={busy} onClick={() => act('delete')}>
              {busy ? <Spinner /> : <Trash2 className="h-4 w-4" aria-hidden />} Delete
            </button>
            <button type="button" className="btn-secondary" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {article.visibility === 'live' && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => act('hide')}>
                {busy ? <Spinner /> : <EyeOff className="h-4 w-4" aria-hidden />} Hide from the site
              </button>
            )}
            {article.visibility === 'hidden' && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => act('restore')}>
                {busy ? <Spinner /> : <RotateCcw className="h-4 w-4" aria-hidden />} Restore
              </button>
            )}
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => setConfirming(true)}>
              <Trash2 className="h-4 w-4" aria-hidden /> Delete
            </button>
          </div>
        )
      )}
    </div>
  );
}

function PublishedTab({
  productId, onNotice,
}: { productId: string; onNotice: (message: string) => void }) {
  const [articles, setArticles] = useState<PublishedArticle[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    get<{ articles: PublishedArticle[] }>(`/modules/${productId}/content-studio/published`)
      .then((data) => { setArticles(data.articles); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId]);

  useEffect(load, [load]);

  // Same bounded poll as the queue, for the same reason: a retraction finishes
  // in the deploy, not in this tab, and a card stuck at "Hiding" after the
  // site already changed would send someone to check the site by hand.
  const pending = Boolean(articles?.some((a) => VISIBILITY_STATE[a.visibility]?.pending));
  useEffect(() => {
    if (!pending) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_CEILING_MS) { clearInterval(timer); return; }
      if (!document.hidden) load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [pending, load]);

  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!articles) return <Loading />;
  if (!articles.length) {
    return (
      <div className="card">
        <Empty
          title="Nothing live yet"
          description="An article appears here once its approval publishes it to the site."
        />
      </div>
    );
  }

  const gone = articles.filter((a) => a.visibility === 'deleted');
  const active = articles.filter((a) => a.visibility !== 'deleted');

  return (
    <div className="space-y-4">
      {/* Same two-abreast layout as the topic queue, for the same reason. */}
      <div className="grid gap-4 md:grid-cols-2">
        {active.map((article) => (
          <PublishedCard
            key={article.id}
            productId={productId}
            article={article}
            onDone={(message) => { onNotice(message); load(); }}
          />
        ))}
      </div>
      {gone.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
            Deleted
            <span className="ml-1.5 opacity-70">{gone.length}</span>
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {gone.map((article) => (
              <PublishedCard
                key={article.id}
                productId={productId}
                article={article}
                onDone={(message) => { onNotice(message); load(); }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -- page ---------------------------------------------------------------------

const TABS = ['Drafts', 'Published', 'Topics', 'Activity'] as const;

export default function ContentStudio({ productId }: { productId: string }) {
  const [queue, setQueue] = useState<Queue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<typeof TABS[number]>('Drafts');
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    get<Queue>(`/modules/${productId}/content-studio/queue`)
      .then((data) => { setQueue(data); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId]);

  useEffect(load, [load]);

  /**
   * Re-read the queue while something is actually running.
   *
   * A draft run takes two to four minutes and its result arrives in the
   * database, not in this tab. Without this the reader presses a button, waits,
   * sees nothing change, and reloads by hand to find out - which is how "the
   * draft was created but does not appear" gets reported for a pipeline that
   * was working correctly the whole time.
   *
   * Three bounds, because a poll is the easiest thing in a codebase to leave
   * running for ever and D1 meters rows read:
   *
   *   - only while a topic is being drafted or a run is open;
   *   - never in a backgrounded tab, which is where a phone leaves it;
   *   - and it gives up after POLL_CEILING_MS, so a run that never reports
   *     back does not leave a tab polling until morning.
   */
  const inFlight = Boolean(queue && (
    queue.topics.some((t) => t.status === 'drafting' && !t.stalled)
    || queue.runs.some((r) => r.status === 'running')));

  useEffect(() => {
    if (!inFlight) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_CEILING_MS) {
        clearInterval(timer);
        return;
      }
      if (!document.hidden) load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [inFlight, load]);

  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!queue) return <Loading />;

  const waiting = queue.drafts.filter((d) => d.status === 'awaiting_review');
  // Approved but not finished: a publish that failed, or a send that stopped
  // part way. These must stay visible or they are lost.
  const stalled = queue.drafts.filter(
    (d) => d.status === 'failed' || d.status === 'publishing'
      || (d.status === 'approved' && d.error));
  const proposed = queue.topics.filter((t) => t.status === 'proposed');
  const counts: Record<string, number> = {
    Drafts: waiting.length + stalled.length,
    Topics: proposed.length,
    Activity: queue.runs.filter((r) => r.status === 'failed').length,
  };

  return (
    /*
      Capped and centred: in the 1400px shell a full-width review card puts
      sixty words of metadata on one line and the buttons a screen-width from
      the text they act on. 5xl is the read-and-decide width; phones are
      untouched by it.
    */
    <div className="mx-auto w-full max-w-5xl space-y-4">
      {notice && (
        <div
          role="status"
          className="flex items-start justify-between gap-3 rounded-lg border border-paper-300
                     bg-white p-3 text-sm shadow-sm dark:border-ink-700 dark:bg-ink-800"
        >
          <p className="min-w-0">{notice}</p>
          <button
            type="button"
            className="shrink-0 rounded-lg p-1 text-ink-400 hover:bg-paper-200 hover:text-ink-600
                       dark:hover:bg-ink-700 dark:hover:text-paper-300"
            aria-label="Dismiss"
            onClick={() => setNotice(null)}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      {/*
        The bar scrolls sideways rather than wrapping: four tabs and their
        counts do not fit a 360px phone at a 44px touch height, and a wrapped
        tab row reads as two rows of buttons, not one control.
      */}
      <div
        className="flex overflow-x-auto rounded-lg border border-paper-300 dark:border-ink-700"
        role="tablist"
      >
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`flex-1 whitespace-nowrap px-3 py-2.5 text-sm font-medium
              first:rounded-l-lg last:rounded-r-lg sm:px-4 ${
              tab === t
                ? 'bg-ink-900 text-paper-100 dark:bg-paper-100 dark:text-ink-900'
                : 'text-ink-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-ink-700'
            }`}
          >
            {t}
            {counts[t] > 0 && (
              <span
                className={`ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full
                  px-1.5 text-[11px] font-semibold ${
                  tab === t
                    ? 'bg-white/20 dark:bg-ink-900/10'
                    : 'bg-paper-200 text-ink-600 dark:bg-ink-700 dark:text-paper-300'
                }`}
              >
                {counts[t]}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'Drafts' && (
        <div className="space-y-4">
          {stalled.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
                Needs attention
              </h2>
              {stalled.map((draft) => (
                <StalledCard
                  key={draft.id}
                  productId={productId}
                  draft={draft}
                  onDone={(message) => { setNotice(message); load(); }}
                />
              ))}
            </div>
          )}
          {waiting.length === 0 && stalled.length === 0 ? (
            <div className="card">
              <Empty
                title="Nothing waiting"
                description="Approved topics are drafted on the pipeline's schedule and appear here to read."
              />
            </div>
          ) : (
            <div className="space-y-4">
              {waiting.map((draft) => (
                <DraftCard
                  key={draft.id}
                  productId={productId}
                  draft={draft}
                  subscribers={queue.subscribers ?? []}
                  onDone={(message) => { setNotice(message); load(); }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'Published' && (
        <PublishedTab productId={productId} onNotice={setNotice} />
      )}

      {tab === 'Topics' && (
        <div className="space-y-4">
          {/*
            Two ways in, and the order says which is which: a subject you
            already have beats one a model has to think of. Both owner-only -
            can_start.research is the owner gate the API applies anyway.
          */}
          {queue.can_start?.research && (
            <div className="space-y-3">
              <AddTopicForm
                productId={productId}
                onDone={(message) => { setNotice(message); load(); }}
              />
              <div className="flex justify-end">
                <FindTopicsButton
                  productId={productId}
                  onDone={(message) => { setNotice(message); load(); }}
                />
              </div>
            </div>
          )}
          {queue.topics.length === 0 ? (
            <div className="card">
              <Empty
                title="No topics proposed"
                description="The research step proposes subjects on its schedule. Nothing is drafted until you approve one."
              />
            </div>
          ) : (
            <>
              {/*
                Grouped rather than one flat list, and every group is drawn even
                when the API returns states this UI has no heading for - the
                last group's `statuses` is a fallback, so a status added to the
                schema later appears somewhere rather than silently vanishing.
                That silent vanishing is the bug this whole section exists to
                fix; reintroducing it in the grouping would be a poor joke.
              */}
              {TOPIC_GROUPS.map((group, i) => {
                const known = TOPIC_GROUPS.flatMap((g) => g.statuses);
                const mine = queue.topics.filter((t) => (
                  group.statuses.includes(t.status)
                  || (i === TOPIC_GROUPS.length - 1 && !known.includes(t.status))
                ));
                if (!mine.length) return null;
                return (
                  <div key={group.key} className="space-y-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                      {group.heading}
                      <span className="ml-1.5 opacity-70">{mine.length}</span>
                    </h2>
                    {/* Topic cards are short - two abreast on a desktop, so a
                        queue of eight is one screen rather than four. */}
                    <div className="grid gap-4 md:grid-cols-2">
                      {mine.map((topic) => (
                        <TopicCard
                          key={topic.id}
                          productId={productId}
                          topic={topic}
                          canDraft={Boolean(queue.can_start?.draft)}
                          onDone={load}
                          onNotice={setNotice}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
              {/*
                The total, so the page can be checked against what a person
                remembers approving. "I counted four and there were six" is the
                report that produced all of this.
              */}
              <p className="pt-1 text-xs text-ink-500 dark:text-ink-400">
                {queue.topics.length} topic{queue.topics.length === 1 ? '' : 's'} in total,
                every state included.
              </p>
            </>
          )}
        </div>
      )}

      {tab === 'Activity' && <Runs runs={queue.runs} />}
    </div>
  );
}
